/**
 * Lead contract v2 — the single source of truth for what a lead document looks
 * like and how raw input (UI form, inbound webhook, future CSV import) is
 * normalized into it.
 *
 * ⚠️ This file is MIRRORED to functions/leadContract.js by
 * scripts/syncPermissionsRegistry.js (run it after every edit; the client sync
 * test fails when the mirror is stale). Keep it pure: no imports, no Firebase,
 * only top-level `export const` / `export function` so the ESM→CJS converter
 * can handle it.
 *
 * See leads_plan.md §2 for the full contract and the alias table.
 */

export const LEAD_SCHEMA_VERSION = 2;

/** Statuses that always exist and can never be removed/renamed by clinics. */
export const LEAD_SYSTEM_STATUSES = ["new", "converted", "lost"];

/** Statuses that mean "closed" — hidden from the default pipeline view. */
export const LEAD_ARCHIVED_STATUSES = ["converted", "lost"];

export const LEAD_LOST_REASONS = [
  { value: "price", label: "המחיר לא התאים" },
  { value: "no_answer", label: "לא ענה/ענתה" },
  { value: "chose_other", label: "בחר/ה במקום אחר" },
  { value: "not_relevant", label: "לא רלוונטי" },
  { value: "other", label: "אחר" },
];

export const LEAD_SOURCE_CHANNELS = [
  { value: "manual", label: "נוצר ידנית" },
  { value: "webhook", label: "נקלט אוטומטית" },
  { value: "booking", label: "עמוד זימון" },
  { value: "import", label: "ייבוא" },
];

/** Sentinel used as createdBy / assignedBy when the server webhook writes a lead. */
export const LEAD_WEBHOOK_ACTOR = "leads_webhook";

export const LEAD_CAPS = {
  name: 100,
  email: 254,
  referralSource: 80,
  campaign: 80,
  notes: 2000,
  lostNote: 500,
  preferredDaysAndTimes: 300,
  utm: 120,
  bodyBytes: 16 * 1024,
};

/**
 * Accepted input keys → canonical field. Matching is case-insensitive and
 * ignores surrounding whitespace. Unknown keys are dropped (never stored).
 */
export const LEAD_FIELD_ALIASES = {
  name: ["name", "full_name", "fullname", "שם", "שם מלא", "שם_מלא"],
  phone: ["phone", "tel", "mobile", "phone_number", "phonenumber", "טלפון", "נייד"],
  email: ["email", "mail", "e_mail", "אימייל", "מייל", "דוא\"ל"],
  referralSource: ["source", "utm_source", "campaign", "מקור", "lead_source", "referral_source", "referralsource"],
  notes: ["notes", "message", "comments", "comment", "הודעה", "הערות", "פרטים"],
  therapistId: ["therapist_id", "therapistid"],
  proposedSessionCost: ["proposedsessioncost", "proposed_session_cost"],
  agreedSessionCost: ["agreedsessioncost", "agreed_session_cost"],
  preferredDaysAndTimes: ["preferreddaysandtimes", "preferred_days_and_times", "preferred_times", "ימים ושעות"],
  status: ["status"],
  lostReason: ["lostreason", "lost_reason"],
  lostNote: ["lostnote", "lost_note"],
  nextFollowUpAt: ["nextfollowupat", "next_follow_up_at", "follow_up_at"],
};

export const LEAD_UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

/** Validation error codes returned by normalizeLeadInput (stable API contract). */
export const LEAD_INPUT_ERRORS = {
  MISSING_NAME: "missing_name",
  MISSING_CONTACT: "missing_contact",
  INVALID_PHONE: "invalid_phone",
  INVALID_EMAIL: "invalid_email",
};

/** Hebrew messages for the UI (the server returns the code; the UI maps it). */
export const LEAD_INPUT_ERROR_LABELS = {
  missing_name: "חסר שם",
  missing_contact: "יש להזין טלפון או אימייל",
  invalid_phone: "מספר הטלפון אינו תקין",
  invalid_email: "כתובת האימייל אינה תקינה",
};

// ---------------------------------------------------------------------------
// Primitive helpers
// ---------------------------------------------------------------------------

/** Removes tags, decodes a few common entities, collapses whitespace. */
export function stripHtml(value) {
  if (value == null) return "";
  const s = String(value)
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'");
  return s
    .split("\n")
    .map((line) => line.replace(/[ \t\r\f\v]+/g, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function clampText(value, max) {
  const s = stripHtml(value);
  if (!s) return null;
  return s.length > max ? s.slice(0, max) : s;
}

/**
 * Israeli phone → E.164 (+972XXXXXXXXX). Accepts 05X-XXXXXXX, 0X-XXXXXXX
 * (landline), 972…, +972…, 00972…, with any spacing/dashes/parentheses.
 * Returns null when the digits do not form a valid Israeli number.
 */
export function normalizeIsraeliPhoneE164(raw) {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (!s) return null;
  // Convert Arabic-Indic / Persian digits if a form sends them.
  s = s.replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660)).replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0));
  const hasPlus = s.startsWith("+");
  let digits = s.replace(/\D/g, "");
  if (!digits) return null;
  if (digits.startsWith("00972")) digits = digits.slice(2);
  if (digits.startsWith("972")) {
    digits = digits.slice(3);
  } else if (hasPlus) {
    // A non-Israeli international number — we only store Israeli numbers.
    return null;
  }
  if (digits.startsWith("0")) digits = digits.slice(1);
  // Mobile 5X-XXXXXXX (9 digits), landline area 2/3/4/8/9 + 7 digits (8 digits),
  // special 7X (e.g. 072/073/074/076/077/079) 9 digits.
  if (/^5\d{8}$/.test(digits)) return `+972${digits}`;
  if (/^7\d{8}$/.test(digits)) return `+972${digits}`;
  if (/^[2-489]\d{7}$/.test(digits)) return `+972${digits}`;
  return null;
}

/** Local display form 05X-XXXXXXX for an E.164 Israeli number (pure, UI helper). */
export function formatIsraeliPhoneLocal(e164) {
  if (!e164) return "";
  const m = String(e164).match(/^\+972(\d{1,2})(\d{7})$/);
  if (!m) return String(e164);
  return `0${m[1]}-${m[2]}`;
}

export function normalizeEmail(raw) {
  if (raw == null) return { value: null, valid: true };
  const s = String(raw).trim().toLowerCase();
  if (!s) return { value: null, valid: true };
  if (s.length > LEAD_CAPS.email) return { value: null, valid: false };
  // Pragmatic RFC-lite check.
  const ok = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s);
  return { value: ok ? s : null, valid: ok };
}

function toNumberOrNull(v) {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * Resolves `raw` (any object: form state, JSON body, parsed form-encoded body)
 * into a flat map of canonical keys using the alias table. Original canonical
 * keys always win over aliases. UTM keys are collected separately.
 */
export function pickLeadFields(raw) {
  const out = {};
  const utm = {};
  if (!raw || typeof raw !== "object") return { fields: out, utm };
  const lowerIndex = {};
  Object.keys(raw).forEach((k) => {
    lowerIndex[String(k).trim().toLowerCase()] = raw[k];
  });
  Object.entries(LEAD_FIELD_ALIASES).forEach(([canonical, aliases]) => {
    if (raw[canonical] !== undefined && raw[canonical] !== null && raw[canonical] !== "") {
      out[canonical] = raw[canonical];
      return;
    }
    for (let i = 0; i < aliases.length; i += 1) {
      const v = lowerIndex[aliases[i].toLowerCase()];
      if (v !== undefined && v !== null && v !== "") {
        out[canonical] = v;
        return;
      }
    }
  });
  LEAD_UTM_KEYS.forEach((k) => {
    const v = lowerIndex[k];
    if (v !== undefined && v !== null && v !== "") {
      utm[k.replace("utm_", "")] = clampText(v, LEAD_CAPS.utm);
    }
  });
  return { fields: out, utm };
}

/**
 * Normalizes raw lead input into the contract's writable fields.
 *
 * @param {object} raw           any shape (form state / webhook body)
 * @param {object} [ctx]
 * @param {"manual"|"webhook"|"booking"|"import"} [ctx.channel="manual"]
 * @param {string|null} [ctx.keyId]           webhook key id
 * @param {string|null} [ctx.keyLabel]        webhook key label
 * @param {string|null} [ctx.ipHash]          hashed IP (never raw)
 * @param {string|null} [ctx.defaultReferralSource]  used when the body has none
 * @param {boolean} [ctx.requireContact=true] webhook requires phone or email; the
 *        UI historically allows a name-only lead, so the client passes false.
 * @param {boolean} [ctx.strictPhone=true]    invalid phone → error (webhook) vs
 *        keep raw string (UI already validated / legacy free text)
 * @returns {{ ok: true, data: object, duplicateKey: string|null } |
 *           { ok: false, error: string, field: string }}
 */
export function normalizeLeadInput(raw, ctx = {}) {
  const {
    channel = "manual",
    keyId = null,
    keyLabel = null,
    ipHash = null,
    defaultReferralSource = null,
    requireContact = true,
    strictPhone = true,
  } = ctx;

  const { fields, utm } = pickLeadFields(raw);

  const name = clampText(fields.name, LEAD_CAPS.name);
  if (!name) return { ok: false, error: LEAD_INPUT_ERRORS.MISSING_NAME, field: "name" };

  let phone = null;
  if (fields.phone != null && String(fields.phone).trim() !== "") {
    phone = normalizeIsraeliPhoneE164(fields.phone);
    if (!phone) {
      if (strictPhone) return { ok: false, error: LEAD_INPUT_ERRORS.INVALID_PHONE, field: "phone" };
      phone = clampText(fields.phone, 40);
    }
  }

  const emailRes = normalizeEmail(fields.email);
  if (!emailRes.valid) return { ok: false, error: LEAD_INPUT_ERRORS.INVALID_EMAIL, field: "email" };
  const email = emailRes.value;

  if (requireContact && !phone && !email) {
    return { ok: false, error: LEAD_INPUT_ERRORS.MISSING_CONTACT, field: "phone" };
  }

  const campaign = clampText(fields.referralSource, LEAD_CAPS.campaign);
  const referralSource = campaign || clampText(defaultReferralSource, LEAD_CAPS.referralSource) || null;

  const statusRaw = fields.status != null ? String(fields.status).trim() : "";
  const status = statusRaw || "new";

  const lostReasonRaw = fields.lostReason != null ? String(fields.lostReason).trim() : "";
  const lostReason = LEAD_LOST_REASONS.some((r) => r.value === lostReasonRaw) ? lostReasonRaw : null;

  const source = {
    channel: LEAD_SOURCE_CHANNELS.some((c) => c.value === channel) ? channel : "manual",
    keyId: keyId || null,
    keyLabel: keyLabel ? clampText(keyLabel, 60) : null,
    campaign: campaign || null,
    utm: Object.keys(utm).length ? utm : null,
    ipHash: ipHash || null,
  };

  const data = {
    schemaVersion: LEAD_SCHEMA_VERSION,
    name,
    phone,
    email,
    referralSource,
    status,
    lostReason: status === "lost" ? lostReason || "other" : null,
    lostNote: status === "lost" ? clampText(fields.lostNote, LEAD_CAPS.lostNote) : null,
    notes: clampText(fields.notes, LEAD_CAPS.notes),
    proposedSessionCost: toNumberOrNull(fields.proposedSessionCost),
    agreedSessionCost: toNumberOrNull(fields.agreedSessionCost),
    preferredDaysAndTimes: clampText(fields.preferredDaysAndTimes, LEAD_CAPS.preferredDaysAndTimes),
    source,
  };

  // Caller decides how to turn this into a Timestamp; we only pass a Date or null.
  if (fields.nextFollowUpAt != null && fields.nextFollowUpAt !== "") {
    const d = fields.nextFollowUpAt instanceof Date ? fields.nextFollowUpAt : new Date(fields.nextFollowUpAt);
    data.nextFollowUpAt = Number.isNaN(d.getTime()) ? null : d;
  } else {
    data.nextFollowUpAt = null;
  }

  const requestedTherapistId =
    fields.therapistId != null && String(fields.therapistId).trim() !== "" ? String(fields.therapistId).trim() : null;

  return { ok: true, data, requestedTherapistId, duplicateKey: phone || email || null };
}

/** Key used to detect "returning lead" (same person already in the clinic). */
export function leadDuplicateKey(lead) {
  if (!lead) return null;
  return lead.phone || lead.email || null;
}

export function isLeadArchived(lead) {
  return !!lead && LEAD_ARCHIVED_STATUSES.includes(lead.status);
}

export function isLeadDeleted(lead) {
  return !!lead && lead.isDeleted === true;
}

export function getLeadSourceChannel(lead) {
  return (lead && lead.source && lead.source.channel) || "manual";
}

export function getLeadLostReasonLabel(value) {
  const r = LEAD_LOST_REASONS.find((x) => x.value === value);
  return r ? r.label : null;
}
